import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map, shareReplay } from 'rxjs/operators';

import { GET_COUNTIES } from '../constants/api-endpoints';

/** A county as the dropdowns consume it. */
export interface CountyOption {
  countyId: number;
  countyName: string;
}

/**
 * The "N/A" choice in every county dropdown.
 *
 * A numeric sentinel rather than `null` because PrimeNG's float label only
 * lifts when the control is "filled", and `null` reads as empty — the label
 * would sit on top of the selected text. Mapped back to `null` at the API edge.
 */
export const NA_COUNTY_ID = 0;

/** The "N/A" option itself, prepended to the loaded list. */
export const NA_COUNTY: CountyOption = { countyId: NA_COUNTY_ID, countyName: 'N/A' };

/** Texas counties, shared by the family, educator and show-admin portals. */
@Injectable({ providedIn: 'root' })
export class CountyService {
  private readonly http = inject(HttpClient);

  /** Counties are static per show, so the first caller's request is reused. */
  private readonly cache = new Map<string, Observable<CountyOption[]>>();

  /**
   * The county list, always leading with "N/A".
   *
   * Answers a failure with the N/A-only list rather than erroring — a county is
   * required but N/A is a valid choice, so a failed lookup must not block a save.
   */
  getCounties(showId: string = 'tsp'): Observable<CountyOption[]> {
    const cached = this.cache.get(showId);
    if (cached) {
      return cached;
    }

    const request$ = this.http.post<any>(GET_COUNTIES(showId), {}).pipe(
      map((response) => {
        // The endpoint has been seen answering in both casings.
        const data = response?.data ?? response;
        if (!Array.isArray(data)) {
          return [NA_COUNTY];
        }
        return [
          NA_COUNTY,
          ...data.map((county: any) => ({
            countyId: county.CountyId ?? county.countyId,
            countyName: county.CountyName ?? county.countyName
          }))
        ];
      }),
      catchError(() => {
        // Not cached, so the next dropdown retries instead of staying N/A-only.
        this.cache.delete(showId);
        return of([NA_COUNTY]);
      }),
      shareReplay({ bufferSize: 1, refCount: false })
    );

    this.cache.set(showId, request$);
    return request$;
  }
}
